import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { appRoutes } from 'config';
import heroProductPreview from 'assets/images/hero-product-preview.png';
import { CtaButton } from './components/CtaButton';
import { PlatformRow } from './components/PlatformRow';

const ChromeGlyph = () => (
  <svg viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="3.4" />
    <path d="M12 8.6h9M8.6 13.7l-4.5 7.8M15.4 13.7l-4.5 7.8" />
  </svg>
);

// The MindFeed clipper popover floating over a dimmed web page.
const ClipperStage = () => {
  const t = useTranslations();
  return (
    <div className={styles.stage}>
      <Image
        src={heroProductPreview}
        alt={t('pages.download.chrome.imageAlt')}
        className={styles.web}
        sizes="(max-width: 1024px) 100vw, 560px"
      />
      <div className={styles.pop.container}>
        <div className={styles.pop.head}>
          <span className={styles.pop.mark} />
          <b className={styles.pop.title}>
            {t('pages.download.chrome.clipperTitle')}
          </b>
        </div>
        <div className={styles.pop.body}>
          <div className={styles.pop.caption}>
            {t('pages.download.chrome.clipperCaption')}
          </div>
          <div className={styles.pop.card}>
            <p className={styles.pop.quote}>
              &ldquo;{t('pages.download.chrome.clipperQuote')}&rdquo;
            </p>
            <div className={styles.pop.src}>
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="9" />
                <path d="M3 12h18" />
              </svg>
              mindfeed.com
            </div>
          </div>
          <div className={styles.pop.sel.row}>
            <span className={styles.pop.sel.on}>
              {t('pages.download.chrome.typeQuote')}
            </span>
            <span className={styles.pop.sel.off}>
              {t('pages.download.chrome.typeLink')}
            </span>
            <span className={styles.pop.sel.off}>
              {t('pages.download.chrome.typeNote')}
            </span>
          </div>
          <div className={styles.pop.save}>
            {t('pages.download.chrome.clipperSave')}
          </div>
        </div>
      </div>
    </div>
  );
};

// Download page — Chrome Extension.
export const Section37 = () => {
  const t = useTranslations();
  return (
    <PlatformRow
      id="chrome"
      reversed
      icon={<ChromeGlyph />}
      tag={t('pages.download.chrome.tag')}
      title={t('pages.download.chrome.title')}
      description={t('pages.download.chrome.description')}
      features={[
        t('pages.download.chrome.feature1'),
        t('pages.download.chrome.feature2'),
        t('pages.download.chrome.feature3'),
      ]}
      actions={
        <CtaButton
          variant="primary"
          href={appRoutes.DOWNLOAD.CHROME_EXTENSION}
          reportLocation="Download Chrome Extension"
          withArrow
        >
          {t('pages.download.chrome.cta')}
        </CtaButton>
      }
      visual={<ClipperStage />}
    />
  );
};

const styles = {
  stage: 'relative z-[2] w-full max-w-[560px]',
  web: [
    'w-full rounded-[13px] border blur-[0.4px] brightness-90',
    'border-[#F0F0F0] shadow-[0_20px_50px_rgba(20,30,60,0.08)]',
    'marketing-dark:border-white/[0.08] marketing-dark:shadow-[0_30px_70px_rgba(2,8,30,0.5)]',
  ].join(' '),
  pop: {
    container: [
      'absolute right-3.5 top-[54px] z-[3] w-[268px] overflow-hidden rounded-[15px] border border-[#eef0f4] bg-white text-left',
      'shadow-[0_14px_36px_rgba(20,30,60,0.12)]',
      'marketing-dark:shadow-[0_30px_70px_rgba(2,8,30,0.5)]',
    ].join(' '),
    head: 'flex items-center gap-[9px] border-b border-[#f0f1f4] px-[15px] py-[13px]',
    mark: 'h-[18px] w-[18px] flex-shrink-0 rounded-[5px] bg-mf-blue',
    title: 'text-[13.5px] font-bold text-[#161E3A]',
    body: 'px-[15px] py-3.5',
    caption:
      'mb-[9px] text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8a8f99]',
    card: 'mb-[11px] rounded-[11px] bg-[#f7f8fa] p-[11px]',
    quote: 'font-serif text-[13px] italic leading-[1.45] text-[#161E3A]',
    src: 'mt-1.5 flex items-center gap-[5px] text-[10.5px] font-medium text-[#9aa1b0] [&>svg]:h-[11px] [&>svg]:w-[11px] [&>svg]:fill-none [&>svg]:stroke-[#9aa1b0] [&>svg]:stroke-2',
    sel: {
      row: 'mb-[13px] flex gap-[7px]',
      on: 'flex-1 rounded-[9px] bg-[#EBF0F7] py-[7px] text-center text-[11px] font-semibold text-mf-blue',
      off: 'flex-1 rounded-[9px] bg-[#f0f1f4] py-[7px] text-center text-[11px] font-semibold text-[#5b606b]',
    },
    save: 'rounded-full bg-mf-blue py-2.5 text-center text-[13px] font-bold text-white',
  },
};
